<template>
  <section class="small-favourite-product">
    <a :href="result.detailPage" class="small-favourite-product__picture" @click="handlerClick">
      <DefImg :result="picture" class="small-favourite-product__image" />
      <GiaPhoto v-if="result.isGia" />
    </a>
    <div class="small-favourite-product__block">
      <a :href="result.detailPage" class="small-favourite-product__main" @click="handlerClick">
        <span class="small-favourite-product__name">{{ result.type }}</span>
        <span v-if="result.collection" class="small-favourite-product__collection">
          {{ result.collection.name }}
        </span>
      </a>
      <div class="small-favourite-product__bottom">
        <div class="small-favourite-product__prices">
          <DefPrice v-if="price && price.printPrice">
            {{ price.printPrice }}
          </DefPrice>
          <DefPriceOld v-if="price && price.printPriceOld">
            {{ price.printPriceOld }}
          </DefPriceOld>
        </div>
        <DefPriceEN>
          {{ result?.price?.printPriceEUR }}
        </DefPriceEN>
        <DefSale v-if="price && price.printDiscountPercent">
          {{ price.printDiscountPercent }}
        </DefSale>
      </div>

      <div class="small-favourite-product__sku"></div>

      <DefBuySecondary :result="result" :disabled="!isProductAvailable(result)" />

      <DefRemove @click="favouriteStore.remove({ id })" />
    </div>
  </section>
</template>

<script lang="ts" setup>
import DefImg from '@components/Def/DefImg/DefImg.vue';
import GiaPhoto from '@components/Product/GiaPhoto.vue';
import DefPrice from '@components/Def/DefPrice/DefPrice.vue';
import DefPriceOld from '@components/Def/DefPriceOld/DefPriceOld.vue';
import DefPriceEN from '@components/Def/DefPrice/DefPriceEN.vue';
import DefSale from '@components/Def/DefSale/DefSale.vue';
import Analytics from '@/utils/Analytics/Analytics';
import DefRemove from '@components/Def/DefRemove/DefRemove.vue';
import { computed } from 'vue';

import { useFavouriteStore } from '@/storePinia/stores/favourite';
import { isProductAvailable } from '@/utils/product/product';
import DefBuySecondary from '@components/Def/DefBuy/DefBuySecondary.vue';

const favouriteStore = useFavouriteStore();

const props = defineProps(['result']);

const picture = computed(() => props.result.picture);
const price = computed(() => props.result.price);
const id = computed(() => props.result?.offer?.id ?? props.result?.id);

const handlerClick = () => {
  Analytics.clickOnProduct(props.result);
};
</script>

<style lang="scss" scoped>
.small-favourite-product {
  position: relative;
  display: grid;
  grid-template-columns: 50px 1fr;
  grid-column-gap: 12px;
  padding: 16px 0;
  @include point-md {
    grid-column-gap: 16px;
  }
  @include point-lg {
    grid-template-columns: 60px 1fr;
  }
  &:not(:last-child) {
    border-bottom: 1px solid var(--border-grey);
  }
  &__picture {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 80px;
    position: relative;
  }
  &__block {
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 16px;
    grid-column-gap: 12px;
    align-items: center;
    grid-template-areas:
      'main buy remove'
      'sku prices prices';
    @include point-md {
      grid-template-areas: 'main sku prices buy remove ';
      grid-template-columns: 1fr 1fr 1fr auto 20px;
      grid-column-gap: 24px;
    }
  }
  &__main {
    display: flex;
    flex-direction: column;
    grid-area: main;
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    grid-area: prices;
  }
  &__prices {
    display: flex;
    flex-direction: column-reverse;
    align-items: flex-end;
    gap: 2px;
    @include point-lg {
      flex-direction: row;
      align-items: center;
      gap: 8px;
    }
  }
  :deep(.def-remove) {
    grid-area: remove;
  }
  &__name {
    margin-bottom: 4px;
    font-size: 16px;
    font-weight: 500;
    line-height: 125%;
    text-transform: uppercase;
  }
  &__collection {
    font-size: rem(11);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-dark-grey);
    margin-bottom: 12px;
    @include point-md {
      font-size: rem(12);
      margin-bottom: 10px;
    }
  }
  :deep(.def-price-old) {
    font-size: 14px;
    font-weight: 500;
    line-height: 125%;
    @include point-md {
      font-size: rem(12);
    }
    @include point-lg {
      font-size: 14px;
    }
  }
  :deep(.def-price) {
    font-weight: 600;
    font-size: rem(16);
    line-height: 125%;
    @include point-md {
      font-size: rem(14);
    }
    @include point-lg {
      font-size: 16px;
    }
  }
  :deep(.def-price-en) {
    font-size: rem(16);
    @include point-md {
      margin-bottom: 8px;
      font-size: rem(18);
    }
  }
  :deep(.def-button-secondary) {
    white-space: nowrap;
    grid-area: buy;
  }
  &__sku {
    grid-area: sku;
    :deep(.def-select-wrapper) {
      display: inline-block;
      width: unset;
      min-width: 120px;
      @include point-lg {
        min-width: 130px;
      }
    }

    :deep(.def-select-wrapper__placeholder) {
      display: none;
    }
    :deep(.def-select-wrapper__front) {
      min-height: 44px;
      justify-content: center;
    }
  }
  :deep(.gia-photo) {
    position: absolute;
    right: 0;
    bottom: 0;
    max-width: 32px;
  }
}
.sku-disabled {
  opacity: 0.6;
}
</style>
